// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import type { TyApiResponse, TyStoreMembership } from "@/types";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The stores this client can reach, and the one place that keeps `useAuth` honest about them.
 *
 * `useAuth.client` is persisted, and nothing else ever refetches it — so ownership granted outside this
 * device's own "create store" flow used to stay invisible until the next sign-in. Every screen that
 * lists stores already asks the API who the client is; reconciling here means all of them do it, and
 * none of them has to remember to.
 */
export default function useStoresList() {
    const syncMembership = useAuth((state) => state.syncMembership);
    const { data, loading, error } = useFetch<TyApiResponse<{ stores: TyStoreMembership[] }>>("auth/stores");

    const stores = data?.data?.stores ?? [];

    useEffect(() => {
        if (!data?.success) return;
        syncMembership(data.data.stores);
    }, [data]);

    return { stores, loading, error };
}
